import Feature from "ol/Feature";
import { getCenter } from "ol/extent";
import { squaredDistance } from "ol/coordinate";
import { primaryAction } from "ol/events/condition";
import PointerInteraction from "ol/interaction/Pointer";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import Circle from "ol/geom/Circle";
import type Geometry from "ol/geom/Geometry";
import { fromCircle } from "ol/geom/Polygon";
import type BaseLayer from "ol/layer/Base";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";

/** Plus court delta angulaire dans ]-π, π]. */
function angleDeltaRad(current: number, previous: number): number {
  let d = current - previous;
  while (d > Math.PI) {
    d -= 2 * Math.PI;
  }
  while (d < -Math.PI) {
    d += 2 * Math.PI;
  }
  return d;
}

function preparePolygonGeometry(feature: Feature, circleSides: number): Geometry | null {
  let geom = feature.getGeometry();
  if (!geom) {
    return null;
  }
  if (geom.getType() === "Circle") {
    const poly = fromCircle(geom as Circle, circleSides);
    feature.setGeometry(poly);
    geom = poly;
  }
  const t = geom.getType();
  if (t !== "Polygon" && t !== "MultiPolygon") {
    return null;
  }
  return geom;
}

export type ShapeTransformOptions = {
  layer: VectorLayer<VectorSource>;
  /** Conversion cercle → polygone si besoin (aligné sur l’éditeur). */
  circleToPolygonSides?: number;
};

/** Rotation autour du centre de la bbox : glisser pour pivoter (incremental). */
export class RotateShapeInteraction extends PointerInteraction {
  private readonly layer_: VectorLayer<VectorSource>;

  private readonly circleSides_: number;

  private anchor_: import("ol/coordinate").Coordinate | null = null;

  private lastAngle_: number | null = null;

  private activeFeature_: Feature | null = null;

  constructor(options: ShapeTransformOptions) {
    super({ stopDown: (handled) => handled });
    this.layer_ = options.layer;
    this.circleSides_ = options.circleToPolygonSides ?? 8;
  }

  handleDownEvent(evt: MapBrowserEvent): boolean {
    if (!primaryAction(evt)) {
      return false;
    }
    const map = evt.map;
    const picked = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
      layerFilter: (layer: BaseLayer) => layer === this.layer_,
      hitTolerance: 8,
    }) as Feature | undefined;
    if (!picked) {
      return false;
    }
    const geom = preparePolygonGeometry(picked, this.circleSides_);
    if (!geom) {
      return false;
    }
    this.activeFeature_ = picked;
    this.anchor_ = getCenter(geom.getExtent());
    this.lastAngle_ = Math.atan2(
      evt.coordinate[1] - this.anchor_[1],
      evt.coordinate[0] - this.anchor_[0],
    );
    return true;
  }

  handleDragEvent(evt: MapBrowserEvent): void {
    if (!this.anchor_ || this.lastAngle_ === null) {
      return;
    }
    const feature = this.activeFeature_;
    if (!feature) {
      return;
    }
    const geom = feature.getGeometry();
    if (!geom) {
      return;
    }
    const cur = Math.atan2(
      evt.coordinate[1] - this.anchor_[1],
      evt.coordinate[0] - this.anchor_[0],
    );
    const delta = angleDeltaRad(cur, this.lastAngle_);
    geom.rotate(delta, this.anchor_);
    this.lastAngle_ = cur;
    feature.changed();
  }

  handleUpEvent(): boolean {
    this.anchor_ = null;
    this.lastAngle_ = null;
    this.activeFeature_ = null;
    return false;
  }
}

/** Mise à l’échelle uniforme depuis le centre géométrique : premier clic sur la forme puis glisser (rayon). */
export class ScaleShapeInteraction extends PointerInteraction {
  private readonly layer_: VectorLayer<VectorSource>;

  private readonly circleSides_: number;

  private anchor_: import("ol/coordinate").Coordinate | null = null;

  private startDistSq_: number | null = null;

  private snapshot_: Geometry | null = null;

  private activeFeature_: Feature | null = null;

  constructor(options: ShapeTransformOptions) {
    super({ stopDown: (handled) => handled });
    this.layer_ = options.layer;
    this.circleSides_ = options.circleToPolygonSides ?? 8;
  }

  handleDownEvent(evt: MapBrowserEvent): boolean {
    if (!primaryAction(evt)) {
      return false;
    }
    const map = evt.map;
    const picked = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
      layerFilter: (layer: BaseLayer) => layer === this.layer_,
      hitTolerance: 8,
    }) as Feature | undefined;
    if (!picked) {
      return false;
    }
    const geom = preparePolygonGeometry(picked, this.circleSides_);
    if (!geom) {
      return false;
    }
    this.activeFeature_ = picked;
    this.anchor_ = getCenter(geom.getExtent());
    this.startDistSq_ = squaredDistance(this.anchor_, evt.coordinate);
    if (this.startDistSq_ < 1e-20) {
      this.anchor_ = null;
      this.startDistSq_ = null;
      this.activeFeature_ = null;
      return false;
    }
    this.snapshot_ = geom.clone();
    return true;
  }

  handleDragEvent(evt: MapBrowserEvent): void {
    if (!this.anchor_ || !this.snapshot_ || this.startDistSq_ === null) {
      return;
    }
    const feature = this.activeFeature_;
    if (!feature) {
      return;
    }
    const curDistSq = squaredDistance(this.anchor_, evt.coordinate);
    const factor = Math.sqrt(curDistSq / this.startDistSq_);
    if (!Number.isFinite(factor) || factor <= 0) {
      return;
    }
    const scaled = this.snapshot_.clone();
    scaled.scale(factor, factor, this.anchor_);
    feature.setGeometry(scaled);
    feature.changed();
  }

  handleUpEvent(): boolean {
    this.anchor_ = null;
    this.startDistSq_ = null;
    this.snapshot_ = null;
    this.activeFeature_ = null;
    return false;
  }
}
